import { FlyerType } from '@/config/constants';
import {
  BodyLargeEmphasized,
  Modal,
} from '@/design-system';
import { css } from '@emotion/css';
import { Divider } from '../Divider';
import type { OptionType } from './FilterOption';
import { FilterOption } from './FilterOption';

type Props = {
  isOpen: boolean;
  title: string;
  viewResultsLabel: string;
  onClearFilters: () => void;
  onShowResults: () => void;
  onToggle: (state: boolean) => void;
  handleFilterToggle: (filterName: string, value: string, description: string) => () => void;
  disableClearAllButton: boolean;
  flyerType: string;
  filterSections: {
    grades_age?: OptionType[],
    types: OptionType[],
  };
  localFilters: {
    grades_age?: string[],
    types: string[],
  }
}

export const ModalFilter: React.FC<Props> = (props) => {
  const { isOpen, title, viewResultsLabel, onClearFilters, onShowResults, disableClearAllButton, onToggle, filterSections, flyerType, localFilters, handleFilterToggle } = props;

  const filterList = () => {
    const filterKeys = Object.keys(filterSections);
    const filtersLength = filterKeys.length;

    return filterKeys.map((key, index) => {
      const isGradesAge = key === 'grades_age';
      const gradesAgeTitle = flyerType === FlyerType.school ? 'Grades' : 'Age';
      const title = isGradesAge ? gradesAgeTitle : key;
      const showDivider = index < filtersLength - 1;
      if (!filterSections[key] || filterSections[key].length === 0) return null;
      const items = filterSections[key];
      const midpoint = Math.ceil(items.length / 2);
      const firstColumn = items.slice(0, midpoint);
      const secondColumn = items.slice(midpoint);
      const ariaLabel = `filter-${key}-label`;

      return (
        <div key={key}>
          <div aria-labelledby={ariaLabel} role='group'>
            <BodyLargeEmphasized className={styles.filterName} id={ariaLabel}>
              {title}
            </BodyLargeEmphasized>
            <div className={styles.filtersContainer}>
              <div className={styles.flexColumn}>
                {firstColumn.map((option) => (
                  <FilterOption
                    key={option.value}
                    {...option}
                    filterName={key}
                    checked={localFilters[key].indexOf(option.value) !== -1}
                    handleToggle={handleFilterToggle}
                  />
                ))}
              </div>
              <div className={styles.flexColumn}>
                {secondColumn.map((option) => (
                  <FilterOption
                    key={option.value}
                    {...option}
                    filterName={key}
                    checked={localFilters[key].indexOf(option.value) !== -1}
                    handleToggle={handleFilterToggle}
                  />
                ))}
              </div>
            </div>
          </div>
          {showDivider && (
            <div className={styles.divider}>
              <Divider />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      confirmButtonText={viewResultsLabel}
      cancelButtonText="Reset Filters"
      onCancel={onClearFilters}
      onConfirm={onShowResults}
      onClose={() => onToggle(false)}
      disableCancelButton={disableClearAllButton}
    >
      <div className={styles.container}>
        {filterList()}
      </div>
    </Modal>
  )
}

const styles = {
  container: css`
    display: flex;
    flex-flow: column nowrap;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  filtersContainer: css`
    display: flex;
    flex-wrap: wrap;
    padding-top: 16px;
  `,
  divider: css`
    padding:24px 0px;
  `,
  flexColumn: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }
  `,
}
